<script setup>
import { ref, computed, getCurrentInstance } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const props = defineProps({
    user: Object,
    currencies: {
        type: Array,
        default: () => []
    },
    languages: {
        type: Array,
        default: () => []
    }
})

const currencyOptions = computed(() => {
    
    if (!props.currencies || !Array.isArray(props.currencies)) {
        console.error('Currencies prop is not an array or is null:', props.currencies);
        return [];
    }
    
    return props.currencies.map(currency => {
        if (typeof currency === 'string') {
            return {
                label: currency,
                value: currency
            };
        }
        return currency;
    });
});

const languageOptions = computed(() => {
    
    if (!props.languages || !Array.isArray(props.languages)) {
        console.error('Languages prop is not an array or is null:', props.languages);
        return [];
    }
    return props.languages;
});

const form = useForm({
    _method: 'PUT',
    first_name: props.user?.first_name || '',
    last_name: props.user?.last_name || '',
    // name: props.user?.name || '',
    birthday: props.user?.birthday || '',
    email: props.user?.email || '',
    phone: props.user?.phone || '',
    coin: props.user?.coin || '',
    locale: props.user?.locale || '',
    photo: null,
})

const verificationLinkSent = ref(false)
const photoPreview = ref(null)
const photoInput = ref(null)

const updateProfileInformation = () => {
    if (photoInput.value?.files[0]) {
        form.photo = photoInput.value.files[0]
    }

    showLoading($t('Updating profile...'));
    const routeName = proxy.route ? proxy.route('client.profile-information.update') : '/profile-information'
    form.post(routeName, {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => {
            clearPhotoFileInput()
            
            const context = 'client'
            const currencyKey = `currency.${context}`
            localStorage.setItem(currencyKey, form.coin);
            const eventName = `currency-changed-${context}`;

            window.dispatchEvent(new CustomEvent(eventName, {
                detail: { currency: form.coin }
            }));

            if (form.locale) {
                const languageKey = `language.${context}`
                localStorage.setItem(languageKey, form.locale);
                const languageEventName = `language-changed-${context}`;
                window.dispatchEvent(new CustomEvent(languageEventName, {
                    detail: { language: form.locale }
                }));
            }
        },
        onFinish: () => hideLoading(),
    })
}

const sendEmailVerification = () => {
    verificationLinkSent.value = true
}

const selectNewPhoto = () => {
    photoInput.value?.click()
}

const updatePhotoPreview = () => {
    const photo = photoInput.value?.files[0]

    if (!photo) return

    const reader = new FileReader()

    reader.onload = (e) => {
        photoPreview.value = e.target.result
    }

    reader.readAsDataURL(photo)
}

const deletePhoto = () => {
    showLoading($t('Removing photo...'));
    const routeName = proxy.route ? proxy.route('client.current-user-photo.destroy') : '/profile-photo'
    router.delete(routeName, {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null
            clearPhotoFileInput()
        },
        onFinish: () => hideLoading(),
    })
}

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null
    }
}
</script>

<template>
    <form @submit.prevent="updateProfileInformation" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- First Name -->
            <div class="field">
                <label for="first_name" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('First name') }} <span class="text-red-500">*</span>
                </label>
                <InputText
                    id="first_name"
                    v-model="form.first_name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.first_name }"
                    autocomplete="given-name"
                    required
                />
                <small v-if="form.errors.first_name" class="p-error">
                    {{ form.errors.first_name }}
                </small>
            </div>

            <!-- Last Name -->
            <div class="field">
                <label for="last_name" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Last name') }} <span class="text-red-500">*</span>
                </label>
                <InputText
                    id="last_name"
                    v-model="form.last_name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.last_name }"
                    autocomplete="family-name"
                    required
                />
                <small v-if="form.errors.last_name" class="p-error">
                    {{ form.errors.last_name }}
                </small>
            </div>

            <!-- Email -->
            <div class="field">
                <label for="email" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Email') }} <span class="text-red-500">*</span>
                </label>
                <InputText
                    id="email"
                    v-model="form.email"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.email }"
                    autocomplete="email"
                    required
                />
                <small v-if="form.errors.email" class="p-error">
                    {{ form.errors.email }}
                </small>
            </div>

            <!-- Birthday -->
            <div class="field">
                <label for="birthday" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Birthday') }}
                </label>
                <Calendar
                    id="birthday"
                    v-model="form.birthday"
                    dateFormat="yy-mm-dd"
                    :showIcon="true"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.birthday }"
                    :placeholder="$t('Select date')"
                />
                <small v-if="form.errors.birthday" class="p-error">
                    {{ form.errors.birthday }}
                </small>
            </div>
        </div>

        <!-- Profile Photo -->
        <div v-if="proxy.$page.props.jetstream?.managesProfilePhotos" class="field">
            <input
                ref="photoInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="updatePhotoPreview"
            >

            <label class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Photo') }}
            </label>

            <div class="flex items-center gap-4">
                <!-- Current Profile Photo -->
                <div v-show="!photoPreview">
                    <Avatar
                        :image="props.user?.profile_photo_url"
                        :label="props.user?.name?.charAt(0)"
                        size="large"
                        shape="circle"
                    />
                </div>

                <!-- New Profile Photo Preview -->
                <div v-show="photoPreview">
                    <Avatar
                        :image="photoPreview"
                        size="large"
                        shape="circle"
                    />
                </div>

                <div class="flex gap-2">
                    <Button
                        type="button"
                        :label="$t('Change Photo')"
                        icon="pi pi-camera"
                        severity="secondary"
                        size="small"
                        @click="selectNewPhoto"
                    />

                    <Button
                        v-if="props.user?.profile_photo_path"
                        type="button"
                        :label="$t('Remove Photo')"
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        size="small"
                        @click="deletePhoto"
                    />
                </div>
            </div>

            <small v-if="form.errors.photo" class="p-error">
                {{ form.errors.photo }}
            </small>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="field">
                <label for="phone" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Phone') }}
                </label>
                <InputText
                    id="phone"
                    v-model="form.phone"
                    type="tel"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.phone }"
                    autocomplete="tel"
                />
                <small v-if="form.errors.phone" class="p-error">
                    {{ form.errors.phone }}
                </small>
            </div>

            <div class="double-field">
                <div class="field">
                    <label for="coin" class="block text-sm font-medium text-surface-700 mb-2">
                        {{ $t('Default currency') }}
                    </label>
                    <Select
                        id="coin"
                        v-model="form.coin"
                        :options="currencyOptions"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('Select currency')"
                        class="w-full"
                        :class="{ 'p-invalid': form.errors.coin }"
                    />
                    <small v-if="form.errors.coin" class="p-error">
                        {{ form.errors.coin }}
                    </small>
                </div>
                <div class="field">
                    <label for="locale" class="block text-sm font-medium text-surface-700 mb-2">
                        {{ $t('Default Language') }}
                    </label>
                    <Select
                        id="locale"
                        v-model="form.locale"
                        :options="languageOptions"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('Select language')"
                        class="w-full"
                        :class="{ 'p-invalid': form.errors.locale }"
                    />
                    <small v-if="form.errors.locale" class="p-error">
                        {{ form.errors.locale }}
                    </small>
                </div>
            </div>
        </div>


        <!-- Actions -->
        <div class="flex items-center gap-4 pt-4">
            <Button
                type="submit"
                :label="form.processing ? $t('Saving...') : $t('Save')"
                icon="pi pi-save"
                :loading="form.processing"
                :disabled="form.processing"
            />

            <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600">
                <i class="pi pi-check-circle"></i>
                <span class="text-sm font-medium">{{ $t('Saved.') }}</span>
            </div>
        </div>
    </form>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.double-field {
    @apply flex flex-col md:flex-row md:gap-6;
}

.double-field .field {
    @apply flex-1;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}

:deep(.p-select) {
    height: 41px;
}

:deep(.p-select .p-select-label) {
    padding: 8px 12px;
    line-height: 1.5;
}
</style>
